import React, { useCallback } from "react";
import { message as antMessage } from "antd/lib";
import { useIntl } from "react-intl";
import { chatApi } from "../../../../services/api";
import UCEvaluationCompleted from "../../UCEvaluationCompleted";

interface UseEvaluationHandlersProps {
  csUuidRef: React.MutableRefObject<string | undefined>; // 保留以兼容现有调用，但不再在 handleEvaluationSubmit 中使用
  setMessages: React.Dispatch<React.SetStateAction<any[]>>;
  styles: any;
}

export const useEvaluationHandlers = ({
  csUuidRef,
  setMessages,
  styles,
}: UseEvaluationHandlersProps) => {
  const intl = useIntl();

  // 处理评价提交
  const handleEvaluationSubmit = useCallback(
    async (rating: number, feedback?: string) => {
      try {
        // 从消息列表中查找评价消息并提取 uuid
        // 使用函数式更新来获取当前消息列表
        let evaluationUuid: string | undefined;
        setMessages((prev) => {
          const evaluationMsg = prev.find(
            (msg) =>
              typeof msg.key === "string" &&
              msg.key.startsWith("evaluation-component-")
          );
          if (evaluationMsg && typeof evaluationMsg.key === "string") {
            evaluationUuid = evaluationMsg.key.replace(
              "evaluation-component-",
              ""
            );
          }
          return prev; // 不改变状态，只是读取
        });

        // 如果找不到 uuid，显示错误
        if (!evaluationUuid) {
          antMessage.error(
            intl.formatMessage({ id: "evaluation.submit.error" })
          );
          return;
        }

        const response = await chatApi.updateEvaluation({
          uuid: evaluationUuid,
          rating,
          feedback,
        });

        if (response.success) {
          // 更新评价提示消息和评价组件消息为已完成状态
          setMessages((prev) =>
            prev.map((msg) => {
              if (typeof msg.key === "string") {
                // 更新评价提示消息
                if (msg.key.startsWith("evaluation-prompt-")) {
                  return {
                    ...msg,
                    content: intl.formatMessage(
                      { id: "evaluation.completed.prompt.message" },
                      {
                        defaultMessage:
                          "感谢您的评价！您的反馈对我们非常重要。",
                      }
                    ),
                  };
                }
                // 更新评价组件消息
                if (msg.key.startsWith("evaluation-component-")) {
                  return {
                    ...msg,
                    content: (
                      <UCEvaluationCompleted
                        rating={rating}
                        feedback={feedback}
                      />
                    ),
                  };
                }
              }
              return msg;
            })
          );
          antMessage.success(
            intl.formatMessage({ id: "evaluation.submit.success" })
          );
        } else {
          antMessage.error(
            intl.formatMessage({ id: "evaluation.submit.error" })
          );
        }
      } catch (error) {
        console.error("评价提交失败:", error);
        antMessage.error(intl.formatMessage({ id: "evaluation.submit.error" }));
      }
    },
    [intl, setMessages]
  );

  // 处理跳过评价
  const handleEvaluationSkip = useCallback(() => {
    // 更新评价组件消息为跳过状态
    setMessages((prev) =>
      prev.map((msg) =>
        typeof msg.key === "string" &&
        msg.key.startsWith("evaluation-component-")
          ? {
              ...msg,
              content: (
                <div className={styles.evaluationSkippedBox}>
                  {intl.formatMessage({ id: "evaluation.skipped" })}
                </div>
              ),
            }
          : msg
      )
    );
  }, [intl, styles.evaluationSkippedBox, setMessages]);

  return {
    handleEvaluationSubmit,
    handleEvaluationSkip,
  };
};
